import React, { useState, useRef } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle, Loader2, ArrowRight, X } from 'lucide-react';
import type { Team, ContactType } from '../../types';
import { bulkCreateContacts, type CreateContactInput } from '../../services/contacts';
import { CONTACT_TYPES, CONTACT_SOURCES } from '../../constants';

interface ImportPageProps {
  team: Team;
}

type ImportStep = 'upload' | 'mapping' | 'preview' | 'importing' | 'complete';

interface ParsedRow {
  [key: string]: string;
}

interface ColumnMapping {
  first_name: string;
  last_name: string;
  company: string;
  email: string;
  phone: string;
  type: string;
  source: string;
  website: string;
  linkedin_url: string;
  title: string;
  external_id: string;
  segment: string;
  icp_fit: string;
  country: string;
  location: string;
  hook: string;
  hook_source_url: string;
  email_subject: string;
  email_body: string;
  linkedin_note: string;
}

const DEFAULT_MAPPING: ColumnMapping = {
  first_name: '',
  last_name: '',
  company: '',
  email: '',
  phone: '',
  type: '',
  source: '',
  website: '',
  linkedin_url: '',
  title: '',
  external_id: '',
  segment: '',
  icp_fit: '',
  country: '',
  location: '',
  hook: '',
  hook_source_url: '',
  email_subject: '',
  email_body: '',
  linkedin_note: '',
};

const MAPPABLE_FIELDS = [
  { key: 'first_name', label: 'First Name', required: false },
  { key: 'last_name', label: 'Last Name', required: false },
  { key: 'company', label: 'Company', required: false },
  { key: 'email', label: 'Email', required: false },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'type', label: 'Type (agent/photographer)', required: true },
  { key: 'source', label: 'Source', required: false },
  { key: 'website', label: 'Website', required: false },
  { key: 'linkedin_url', label: 'LinkedIn URL', required: false },
  { key: 'title', label: 'Title', required: false },
  { key: 'external_id', label: 'Prospect ID', required: false },
  { key: 'segment', label: 'Segment', required: false },
  { key: 'icp_fit', label: 'ICP fit', required: false },
  { key: 'country', label: 'Country', required: false },
  { key: 'location', label: 'Location', required: false },
  { key: 'hook', label: 'Hook', required: false },
  { key: 'hook_source_url', label: 'Hook source URL', required: false },
  { key: 'email_subject', label: 'Email subject', required: false },
  { key: 'email_body', label: 'Email body', required: false },
  { key: 'linkedin_note', label: 'LinkedIn note', required: false },
];

const parseCSV = (text: string): { headers: string[]; rows: ParsedRow[] } => {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return { headers: [], rows: [] };

  const headers = lines[0].split(',').map((h) => h.trim().replace(/^["']|["']$/g, ''));
  const rows: ParsedRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map((v) => v.trim().replace(/^["']|["']$/g, ''));
    const row: ParsedRow = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    rows.push(row);
  }

  return { headers, rows };
};

const guessMapping = (headers: string[]): ColumnMapping => {
  const mapping = { ...DEFAULT_MAPPING };
  const lowerHeaders = headers.map((h) => h.toLowerCase());

  const mappings: { field: keyof ColumnMapping; patterns: string[] }[] = [
    { field: 'first_name', patterns: ['first_name', 'firstname', 'first name', 'given name'] },
    { field: 'last_name', patterns: ['last_name', 'lastname', 'last name', 'surname', 'family name'] },
    { field: 'company', patterns: ['company', 'organisation', 'organization', 'business', 'agency'] },
    { field: 'email', patterns: ['email', 'e-mail', 'mail'] },
    { field: 'phone', patterns: ['phone', 'telephone', 'mobile', 'tel', 'cell'] },
    { field: 'type', patterns: ['type', 'contact type', 'lead type'] },
    { field: 'source', patterns: ['source', 'lead source', 'origin'] },
    { field: 'website', patterns: ['website', 'url', 'web', 'site'] },
    { field: 'linkedin_url', patterns: ['linkedin', 'linkedin_url', 'linkedin url'] },
    { field: 'title', patterns: ['title', 'job title', 'role'] },
    { field: 'external_id', patterns: ['external_id', 'prospect_id', 'prospect id'] },
    { field: 'segment', patterns: ['segment', 'icp segment'] },
    { field: 'icp_fit', patterns: ['icp', 'icp_fit', 'fit'] },
    { field: 'country', patterns: ['country'] },
    { field: 'location', patterns: ['location', 'city'] },
    { field: 'hook', patterns: ['hook'] },
    { field: 'hook_source_url', patterns: ['hook_source', 'source_url', 'hook url'] },
    { field: 'email_subject', patterns: ['email_subject', 'subject'] },
    { field: 'email_body', patterns: ['email_body', 'email body', 'body'] },
    { field: 'linkedin_note', patterns: ['linkedin_note', 'linkedin note', 'connect note'] },
  ];

  mappings.forEach(({ field, patterns }) => {
    for (const pattern of patterns) {
      const index = lowerHeaders.findIndex((h) => h.includes(pattern));
      if (index !== -1) {
        mapping[field] = headers[index];
        break;
      }
    }
  });

  return mapping;
};

const isValidEmail = (email: string): boolean => {
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const ImportPage: React.FC<ImportPageProps> = ({ team }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<ImportStep>('upload');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>(DEFAULT_MAPPING);
  const [defaultType, setDefaultType] = useState<ContactType>('agent');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ created: number; updated: number; skipped: number; errors: string[] } | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const { headers: parsedHeaders, rows: parsedRows } = parseCSV(text);
      
      if (parsedHeaders.length === 0 || parsedRows.length === 0) {
        alert('Could not parse CSV file. Please check the format.');
        return;
      }

      setHeaders(parsedHeaders);
      setRows(parsedRows);
      setMapping(guessMapping(parsedHeaders));
      setStep('mapping');
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type === 'text/csv') {
      const input = fileInputRef.current;
      if (input) {
        const dt = new DataTransfer();
        dt.items.add(file);
        input.files = dt.files;
        handleFileSelect({ target: input } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const getMappedValue = (row: ParsedRow, field: keyof ColumnMapping): string => {
    const column = mapping[field];
    return column ? row[column] || '' : '';
  };

  const previewData = rows.slice(0, 5).map((row) => ({
    first_name: getMappedValue(row, 'first_name'),
    last_name: getMappedValue(row, 'last_name'),
    company: getMappedValue(row, 'company'),
    email: getMappedValue(row, 'email'),
    phone: getMappedValue(row, 'phone'),
    type: getMappedValue(row, 'type') || defaultType,
    source: getMappedValue(row, 'source'),
  }));

  const validationErrors: string[] = [];
  rows.forEach((row, index) => {
    const email = getMappedValue(row, 'email');
    if (email && !isValidEmail(email)) {
      validationErrors.push(`Row ${index + 2}: Invalid email "${email}"`);
    }
    const type = getMappedValue(row, 'type') || defaultType;
    if (!['agent', 'photographer'].includes(type.toLowerCase())) {
      validationErrors.push(`Row ${index + 2}: Invalid type "${type}" (must be agent or photographer)`);
    }
  });

  const handleImport = async () => {
    if (validationErrors.length > 0) {
      alert('Please fix validation errors before importing.');
      return;
    }

    setStep('importing');
    setImporting(true);

    const contacts: CreateContactInput[] = rows.map((row) => {
      const rawType = getMappedValue(row, 'type').toLowerCase() || defaultType;
      const type: ContactType = rawType === 'photographer' ? 'photographer' : 'agent';
      
      return {
        team_id: team.id,
        type,
        first_name: getMappedValue(row, 'first_name') || null,
        last_name: getMappedValue(row, 'last_name') || null,
        company: getMappedValue(row, 'company') || null,
        email: getMappedValue(row, 'email') || null,
        phone: getMappedValue(row, 'phone') || null,
        website: getMappedValue(row, 'website') || null,
        linkedin_url: getMappedValue(row, 'linkedin_url') || null,
        source: getMappedValue(row, 'source') || 'outbound_prospecting',
        status: (getMappedValue(row, 'email_body') || getMappedValue(row, 'linkedin_note')) ? 'drafted' : 'new',
        title: getMappedValue(row, 'title') || null,
        external_id: getMappedValue(row, 'external_id') || null,
        segment: (['agency_marketing', 'photographer', 'solo_agent', 'other'].includes(getMappedValue(row, 'segment'))
          ? getMappedValue(row, 'segment')
          : type === 'photographer' ? 'photographer' : 'agency_marketing') as CreateContactInput['segment'],
        icp_fit: (['strong', 'maybe', 'weak'].includes(getMappedValue(row, 'icp_fit'))
          ? getMappedValue(row, 'icp_fit')
          : null) as CreateContactInput['icp_fit'],
        country: getMappedValue(row, 'country') || null,
        location: getMappedValue(row, 'location') || null,
        hook: getMappedValue(row, 'hook') || null,
        hook_source_url: getMappedValue(row, 'hook_source_url') || null,
        email_subject: getMappedValue(row, 'email_subject') || null,
        email_body: getMappedValue(row, 'email_body') || null,
        linkedin_note: getMappedValue(row, 'linkedin_note') || null,
        drafted_on: (getMappedValue(row, 'email_body') || getMappedValue(row, 'linkedin_note'))
          ? new Date().toISOString()
          : null,
      };
    });

    const importResult = await bulkCreateContacts(contacts);
    setResult(importResult);
    setImporting(false);
    setStep('complete');
  };

  const handleReset = () => {
    setStep('upload');
    setFileName('');
    setHeaders([]);
    setRows([]);
    setMapping(DEFAULT_MAPPING);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="animate-fadeIn max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Import Leads</h1>
        <p className="text-slate-400 text-sm mt-1">
          Upload a CSV file to bulk import contacts
        </p>
      </div>

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div
          className="bg-slate-900 rounded-xl border-2 border-dashed border-slate-700 hover:border-slate-600 transition-colors p-12 text-center"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <Upload size={48} className="mx-auto mb-4 text-slate-500" />
          <h3 className="text-lg font-medium text-white mb-2">Upload CSV File</h3>
          <p className="text-slate-400 text-sm mb-6">
            Drag and drop or click to select a file
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-6 py-3 bg-orange-600 hover:bg-orange-500 text-white font-medium rounded-lg transition-colors"
          >
            Select CSV File
          </button>
          <p className="text-slate-500 text-xs mt-4">
            Supported columns: first_name, last_name, company, email, phone, type, source
          </p>
        </div>
      )}

      {/* Step 2: Mapping */}
      {step === 'mapping' && (
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <FileText size={20} className="text-orange-500" />
              <div>
                <h3 className="font-medium text-white">{fileName}</h3>
                <p className="text-sm text-slate-400">{rows.length} rows found</p>
              </div>
            </div>
            <button
              onClick={handleReset}
              className="p-2 text-slate-400 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <h4 className="font-medium text-white mb-4">Map Columns</h4>
          <div className="grid grid-cols-2 gap-4 mb-6">
            {MAPPABLE_FIELDS.map(({ key, label, required }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  {label} {required && <span className="text-red-400">*</span>}
                </label>
                <select
                  value={mapping[key as keyof ColumnMapping]}
                  onChange={(e) => setMapping({ ...mapping, [key]: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">— Not mapped —</option>
                  {headers.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {!mapping.type && (
            <div className="mb-6 p-4 bg-slate-800 rounded-lg">
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Default Type (when not mapped)
              </label>
              <select
                value={defaultType}
                onChange={(e) => setDefaultType(e.target.value as ContactType)}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {CONTACT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          )}

          <h4 className="font-medium text-white mb-4">Preview (first 5 rows)</h4>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-2 px-3 text-slate-400">Name</th>
                  <th className="text-left py-2 px-3 text-slate-400">Company</th>
                  <th className="text-left py-2 px-3 text-slate-400">Email</th>
                  <th className="text-left py-2 px-3 text-slate-400">Phone</th>
                  <th className="text-left py-2 px-3 text-slate-400">Type</th>
                </tr>
              </thead>
              <tbody>
                {previewData.map((row, i) => (
                  <tr key={i} className="border-b border-slate-800">
                    <td className="py-2 px-3 text-white">{row.first_name} {row.last_name}</td>
                    <td className="py-2 px-3 text-slate-300">{row.company}</td>
                    <td className="py-2 px-3 text-slate-300">{row.email}</td>
                    <td className="py-2 px-3 text-slate-300">{row.phone}</td>
                    <td className="py-2 px-3 text-slate-300 capitalize">{row.type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {validationErrors.length > 0 && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-red-400 mb-2">
                <AlertCircle size={16} />
                <span className="font-medium">Validation Errors</span>
              </div>
              <ul className="text-sm text-red-400 space-y-1">
                {validationErrors.slice(0, 5).map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
                {validationErrors.length > 5 && (
                  <li>...and {validationErrors.length - 5} more errors</li>
                )}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={validationErrors.length > 0}
              className="flex items-center gap-2 px-6 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-600 text-white font-medium rounded-lg transition-colors"
            >
              Import {rows.length} Leads
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Importing */}
      {step === 'importing' && (
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-12 text-center">
          <Loader2 size={48} className="mx-auto mb-4 text-orange-500 animate-spin" />
          <h3 className="text-lg font-medium text-white mb-2">Importing Leads...</h3>
          <p className="text-slate-400 text-sm">
            Please wait while we process your data
          </p>
        </div>
      )}

      {/* Step 4: Complete */}
      {step === 'complete' && result && (
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-12 text-center">
          <CheckCircle size={48} className="mx-auto mb-4 text-emerald-500" />
          <h3 className="text-lg font-medium text-white mb-2">Import Complete</h3>
          
          <div className="flex justify-center gap-8 my-6">
            <div>
              <p className="text-3xl font-bold text-emerald-400">{result.created}</p>
              <p className="text-sm text-slate-400">Created</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-sky-400">{result.updated}</p>
              <p className="text-sm text-slate-400">Updated</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-amber-400">{result.skipped}</p>
              <p className="text-sm text-slate-400">Skipped (duplicates)</p>
            </div>
            {result.errors.length > 0 && (
              <div>
                <p className="text-3xl font-bold text-red-400">{result.errors.length}</p>
                <p className="text-sm text-slate-400">Errors</p>
              </div>
            )}
          </div>

          {result.errors.length > 0 && (
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-left max-w-md mx-auto">
              <p className="text-sm font-medium text-red-400 mb-2">Errors:</p>
              <ul className="text-xs text-red-400 space-y-1">
                {result.errors.slice(0, 5).map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          <button
            onClick={handleReset}
            className="mt-6 px-6 py-2 bg-orange-600 hover:bg-orange-500 text-white font-medium rounded-lg transition-colors"
          >
            Import More Leads
          </button>
        </div>
      )}
    </div>
  );
};

export default ImportPage;
